import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  TrashIcon,
  PencilIcon,
  LinkIcon,
  ArrowPathIcon,
  ClockIcon,
  CubeIcon,
} from '@heroicons/react/24/outline'
import { twinApi, assetsApi, type TwinProject, type TwinModelMetadata, type TwinConversionStatus } from '../../lib/api'
import { TwinViewer, TwinStatusBadge } from '../../components/TwinViewer'
import { useTranslation } from '../../hooks/useTranslation'

type TabType = 'viewer' | 'metadata' | 'history'

export function TwinProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState<TabType>('viewer')
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Fetch project details
  const { data: project, isLoading, error } = useQuery({
    queryKey: ['twin-project', id],
    queryFn: () => twinApi.getProject(id!),
    enabled: !!id,
    retry: 1,
  })

  // Fetch metadata when project is ready
  const { data: metadata } = useQuery({
    queryKey: ['twin-metadata', id],
    queryFn: () => twinApi.getModelMetadata(id!),
    enabled: !!id && project?.status === 'ready',
    retry: 1,
  })

  // Fetch conversion history
  const { data: conversionHistory } = useQuery({
    queryKey: ['twin-conversions', id],
    queryFn: () => twinApi.getConversionHistory(id!),
    enabled: !!id && activeTab === 'history',
    retry: 1,
  })

  // Find linked asset (if any)
  const { data: linkedAsset } = useQuery({
    queryKey: ['assets', 'by-twin', id],
    queryFn: async () => {
      // Search assets for one linked to this twin project
      const response = await assetsApi.list({ filters: { twinProjectId: id! } })
      return response.data?.[0] || null
    },
    enabled: !!id,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string }) =>
      twinApi.updateProject(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twin-project', id] })
      queryClient.invalidateQueries({ queryKey: ['twin-projects'] })
      setIsEditing(false)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => twinApi.deleteProject(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twin-projects'] })
      navigate('/twin')
    },
  })

  const handleStartEdit = () => {
    setEditName(project?.name || '')
    setEditDescription(project?.description || '')
    setIsEditing(true)
  }

  const handleSaveEdit = () => {
    updateMutation.mutate({
      name: editName,
      description: editDescription,
    })
  }

  const handleDelete = () => {
    deleteMutation.mutate()
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-24"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !project) {
    return (
      <div className="p-4 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <Link
            to="/twin"
            className="inline-flex items-center gap-x-2 text-sm font-medium text-gray-500 hover:text-gray-700 mb-6"
          >
            <ArrowLeftIcon className="size-4" />
            {t('twin.projects.title')}
          </Link>
          <div className="rounded-lg border border-dashed border-red-300 bg-red-50 p-12 text-center">
            <CubeIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('common.notFound')}</h3>
            <p className="mt-1 text-sm text-gray-500">The Twin project could not be found.</p>
          </div>
        </div>
      </div>
    )
  }

  const tabs = [
    { key: 'viewer' as const, label: t('twin.detail.viewer'), icon: CubeIcon },
    { key: 'metadata' as const, label: t('twin.detail.metadata'), icon: CubeIcon },
    { key: 'history' as const, label: t('audit.history'), icon: ClockIcon },
  ]

  return (
    <div className="p-4 lg:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Back link */}
        <div className="mb-6">
          <Link
            to="/twin"
            className="inline-flex items-center gap-x-2 text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="size-4" />
            {t('twin.projects.title')}
          </Link>
        </div>

        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between mb-8">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-4 max-w-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.name')}
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.description')}
                  </label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center px-3 py-2 text-sm font-semibold text-white bg-brand-500 rounded-md hover:bg-brand-600 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? t('common.saving') : t('common.save')}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                  <TwinStatusBadge status={project.status} />
                </div>
                {project.description && (
                  <p className="mt-1 text-sm text-gray-500">{project.description}</p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  {t('fields.createdAt')}: {new Date(project.createdAt).toLocaleString()}
                </p>
              </>
            )}
          </div>

          {!isEditing && (
            <div className="mt-4 sm:mt-0 flex gap-2">
              <button
                onClick={handleStartEdit}
                className="inline-flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                <PencilIcon className="size-4" />
                {t('common.edit')}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="inline-flex items-center gap-x-2 rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-100"
              >
                <TrashIcon className="size-4" />
                {t('common.delete')}
              </button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900">{t('twin.detail.delete')}</h3>
              <p className="mt-2 text-sm text-gray-500">{t('twin.detail.confirmDelete')}</p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="px-3 py-2 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? t('common.loading') : t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main content grid */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* 3D Viewer - takes 2 columns */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex gap-x-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.key
                        ? 'border-brand-500 text-brand-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab content */}
            {activeTab === 'viewer' && (
              <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                {project.status === 'ready' ? (
                  <TwinViewer
                    projectId={project.id}
                    className="min-h-[500px]"
                    onPartClick={(partId, partName, bomData) => {
                      console.log('Part clicked:', partId, partName, bomData)
                    }}
                  />
                ) : project.status === 'processing' ? (
                  <div className="min-h-[500px] flex flex-col items-center justify-center bg-gray-900">
                    <ArrowPathIcon className="h-10 w-10 text-brand-400 animate-spin" />
                    <p className="mt-4 text-sm text-gray-300">{t('twin.viewer.converting')}</p>
                  </div>
                ) : project.status === 'failed' ? (
                  <div className="min-h-[500px] flex flex-col items-center justify-center bg-gray-100">
                    <CubeIcon className="h-12 w-12 text-red-400" />
                    <p className="mt-4 text-sm text-gray-700 font-medium">{t('twin.viewer.conversionFailed')}</p>
                    <p className="mt-1 text-xs text-gray-500">{t('twin.viewer.tryDifferentFormat')}</p>
                  </div>
                ) : (
                  <div className="min-h-[500px] flex flex-col items-center justify-center bg-gray-100">
                    <CubeIcon className="h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-sm text-gray-500">{t('twin.viewer.noModel')}</p>
                    <p className="mt-1 text-xs text-gray-400">{t('twin.viewer.uploadPrompt')}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'metadata' && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('twin.detail.metadata')}</h2>

                {project.status === 'ready' && metadata ? (
                  <div className="space-y-6">
                    {/* Part count */}
                    <div className="flex justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-500">{t('twin.detail.partCount')}</span>
                      <span className="text-sm font-medium text-gray-900">{metadata.partCount || 0}</span>
                    </div>

                    {/* Bounding box */}
                    {metadata.boundingBox && (
                      <div className="py-3 border-b border-gray-100">
                        <span className="text-sm text-gray-500">Bounding Box</span>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
                          <div className="bg-gray-50 p-2 rounded">
                            <span className="text-gray-400">Min:</span>{' '}
                            [{metadata.boundingBox.min.join(', ')}]
                          </div>
                          <div className="bg-gray-50 p-2 rounded">
                            <span className="text-gray-400">Max:</span>{' '}
                            [{metadata.boundingBox.max.join(', ')}]
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Parts list */}
                    {metadata.parts && metadata.parts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Parts</h3>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Part #</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {metadata.parts.map((part) => (
                                <tr key={part.id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-sm text-gray-900">{part.name}</td>
                                  <td className="px-3 py-2 text-sm text-gray-500 font-mono">{part.partNumber || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : project.status === 'processing' ? (
                  <div className="text-center py-8">
                    <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                    <p className="mt-2 text-sm text-gray-500">{t('twin.viewer.converting')}</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500">
                    {project.status === 'failed' ? t('twin.viewer.conversionFailed') : t('twin.viewer.noModel')}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('audit.history')}</h2>

                {conversionHistory && conversionHistory.length > 0 ? (
                  <div className="space-y-4">
                    {conversionHistory.map((conversion, index) => (
                      <div key={index} className="flex gap-4 py-3 border-b border-gray-100 last:border-0">
                        <div className="flex-shrink-0">
                          <div className={`w-2 h-2 mt-2 rounded-full ${
                            conversion.status === 'completed' ? 'bg-green-500' :
                            conversion.status === 'failed' ? 'bg-red-500' :
                            conversion.status === 'processing' ? 'bg-yellow-500' :
                            'bg-gray-300'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {conversion.status.charAt(0).toUpperCase() + conversion.status.slice(1)}
                          </p>
                          {conversion.message && (
                            <p className="text-sm text-gray-500">{conversion.message}</p>
                          )}
                          {conversion.error && (
                            <p className="text-sm text-red-600">{conversion.error}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {conversion.startedAt && new Date(conversion.startedAt).toLocaleString()}
                            {conversion.completedAt && (
                              <> — {new Date(conversion.completedAt).toLocaleString()}</>
                            )}
                          </p>
                        </div>
                        {conversion.progress !== undefined && (
                          <div className="text-sm text-gray-500">
                            {conversion.progress}%
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500">
                    {t('audit.noHistory')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Info Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('twin.detail.title')}</h2>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('common.status')}</dt>
                  <dd className="mt-1">
                    <TwinStatusBadge status={project.status} />
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('twin.detail.partCount')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {project.status === 'ready' ? (metadata?.partCount || 0) : '--'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('twin.detail.linkedAsset')}</dt>
                  <dd className="mt-1">
                    {linkedAsset ? (
                      <Link
                        to={`/assets/${linkedAsset.id}`}
                        className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
                      >
                        <LinkIcon className="h-4 w-4" />
                        {linkedAsset.serialNumber}
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-500">{t('twin.detail.noAsset')}</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('fields.createdAt')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(project.createdAt).toLocaleDateString()}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('fields.date')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(project.updatedAt).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Actions Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('common.actions')}</h3>
              <div className="space-y-2">
                {!linkedAsset && (
                  <Link
                    to="/assets"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <LinkIcon className="h-4 w-4" />
                    {t('twin.detail.linkAsset')}
                  </Link>
                )}
                {linkedAsset && (
                  <Link
                    to={`/assets/${linkedAsset.id}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-brand-500 rounded-md hover:bg-brand-600"
                  >
                    <CubeIcon className="h-4 w-4" />
                    View Asset
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
